import { blockComponent, blocksOn, type FormPage } from '@ayumia/component-runtime';
import { text } from '../../../app/i18n';
import { blockRegion, sectionRegion } from '../../../work/FormLayoutParts';
import { axisOf, boxOf, lineAt, lineInside, sideOf, type Box } from '../../editor/dnd/geometry';
import { blocksOf, declaredPages, type Template } from '../layout-model';
import { currentLayout, fr, type EditorRegistry } from '../layout-registry';
import type { DragKind, LabelPart } from './drop-targets';

/**
 * Lecture du rendu réel (`TemplateForm`) pour le canevas de l'éditeur (ADR-146 §5.3, addendum (13)) : quel
 * élément du gabarit porte un nœud du DOM, quel nœud montre un élément, quelle page un onglet, une étape ou
 * « Suivant » désigne, quel libellé un double-clic modifie, où tomberait un dépôt. Le DOM n'est que lu : le
 * rendu réel ne porte aucun attribut propre à l'éditeur ; seuls ceux qu'il pose déjà servent.
 */

type Section = Template['sections'][number];

export interface CanvasContext {
  template: Template;
  registry: EditorRegistry;
  host: HTMLElement;
  /** Page montrée par l'aperçu. */
  page: string | undefined;
  /** Pages visibles de l'aperçu, dans l'ordre (celles de `TemplateForm`). */
  pages: readonly FormPage[];
}

const grouped = (section: Section): boolean => section.repeat !== undefined || section.series !== undefined;
const escapeId = (value: string): string => (typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(value) : value.replace(/["\\#.:[\]]/g, '\\$&'));
const textOf = (element: Element | null | undefined): string => (element?.textContent ?? '').replace(/\s+/g, ' ').trim();

const declared = (template: Template): boolean => declaredPages(template).length > 0;
const currentIndex = (context: CanvasContext): number => Math.max(0, context.pages.findIndex((page) => page.page_id === context.page));
const shownPage = (context: CanvasContext): string | undefined => context.pages[currentIndex(context)]?.page_id;

/** Champs des parties simples : identifiant → chemin (`/sections/0/fields/2`). */
function plainFields(template: Template): Map<string, string> {
  const fields = new Map<string, string>();
  for (const [s, section] of template.sections.entries()) {
    if (grouped(section)) continue;
    section.fields.forEach((entry, f) => fields.set(entry.field.field_id, `/sections/${s}/fields/${f}`));
  }
  return fields;
}

/**
 * Champ désigné par un identifiant du rendu : `input-<champ>`, son libellé `input-<champ>-label`, une tuile
 * `field-<champ>-<option>`, la cible ou la preuve du champ. Le plus long identifiant connu l'emporte.
 */
function fieldOfId(fields: ReadonlyMap<string, string>, id: string): string | null {
  const match = /^(?:input|field|target|evidence|error)-(.+)$/.exec(id);
  if (match === null) return null;
  const rest = match[1]!;
  if (fields.has(rest)) return fields.get(rest)!;
  let best: string | undefined;
  for (const fieldId of fields.keys()) if (rest.startsWith(`${fieldId}-`) && (best === undefined || fieldId.length > best.length)) best = fieldId;
  return best === undefined ? null : fields.get(best)!;
}

/** Champ porté par un bloc de champ du rendu (`.ay-form__entry`), par les identifiants qu'il pose. */
function fieldOfEntry(template: Template, entry: Element): string | null {
  const fields = plainFields(template);
  for (const element of [entry, ...entry.querySelectorAll('[id]')]) {
    const pointer = element.id === '' ? null : fieldOfId(fields, element.id);
    if (pointer !== null) return pointer;
  }
  return null;
}

/** Bloc de champ du rendu qui montre un champ. */
function entryOfField(host: Element, fieldId: string): Element | null {
  const control = host.querySelector(`#${escapeId(`input-${fieldId}`)}, #${escapeId(`input-${fieldId}-label`)}, [id^="field-${fieldId}-"]`);
  return control?.closest('.ay-form__entry') ?? null;
}

export const cssEscape = (value: string): string => escapeId(value);

/**
 * Rendus d'une partie par sa région de mise en page : la bande de champs (`strip-<partie>`), la liste et le
 * détail d'une série (`list-<partie>`, `detail-<partie>`) ne posent pas `data-section` ; on les reconnaît à
 * leur classe et à leur repère de test, qui nomme la partie.
 */
const REGION_RENDERS: ReadonlyArray<{ selector: string; prefix: string }> = [
  { selector: '.ay-fv2-strip', prefix: 'strip-' },
  { selector: '.ay-fv2-list', prefix: 'list-' },
  { selector: '.ay-fv2-detail', prefix: 'detail-' },
];

/** Partie montrée par un nœud du rendu : `data-section`, sinon un rendu de région (bande, liste, détail). */
function sectionOfNode(template: Template, node: Element): string | null {
  const known = (id: string | null | undefined): string | null => (id !== null && id !== undefined && template.sections.some((section) => section.section_id === id) ? id : null);
  const placed = known(node.getAttribute('data-section'));
  if (placed !== null) return placed;
  const testId = node.getAttribute('data-testid') ?? '';
  for (const render of REGION_RENDERS) if (testId.startsWith(render.prefix) && node.matches(render.selector)) return known(testId.slice(render.prefix.length));
  return null;
}

/** Nœuds du rendu qui montrent une partie (sa carte placée, ou sa bande, sa liste et son détail). */
function sectionNodes(host: Element, sectionId: string): Element[] {
  const id = escapeId(sectionId);
  return [...host.querySelectorAll(`[data-section="${id}"], ${REGION_RENDERS.map((render) => `${render.selector}[data-testid="${render.prefix}${id}"]`).join(', ')}`)];
}

/**
 * Champ porté par une cellule marquée `data-field` (bande de champs, détail d'une série) : la partie qui
 * l'entoure dit où chercher l'identifiant ; un champ d'une série se désigne comme un champ simple.
 */
function fieldOfCell(context: CanvasContext, element: Element): string | null {
  const cell = element.closest('[data-field]');
  if (cell === null || !context.host.contains(cell)) return null;
  const fieldId = cell.getAttribute('data-field');
  for (let node = cell.parentElement; node !== null && context.host.contains(node); node = node.parentElement) {
    const sectionId = sectionOfNode(context.template, node);
    if (sectionId === null) continue;
    const s = context.template.sections.findIndex((section) => section.section_id === sectionId);
    const f = context.template.sections[s]!.fields.findIndex((entry) => entry.field.field_id === fieldId);
    return f < 0 ? null : `/sections/${s}/fields/${f}`;
  }
  return null;
}

/** Partie d'un conteneur sans `data-section` (rendu sans mise en page) : par ses champs, sinon par son titre. */
function sectionOfContainer(template: Template, element: Element): string | null {
  if (element.matches('fieldset.ay-form__section') && !element.classList.contains('ay-form__item')) {
    const fields = plainFields(template);
    for (const control of element.querySelectorAll('[id]')) {
      const pointer = fieldOfId(fields, control.id);
      if (pointer !== null) return template.sections[Number(pointer.split('/')[2])]!.section_id;
    }
    const legend = textOf(element.querySelector(':scope > legend'));
    return template.sections.find((section) => !grouped(section) && text(section.label, 'fr', section.section_id) === legend)?.section_id ?? null;
  }
  const title = element.querySelector(':scope > h3.ay-form__repeat-title');
  if (title !== null) return template.sections.find((section) => grouped(section) && text(section.label, 'fr', section.section_id) === textOf(title))?.section_id ?? null;
  return null;
}

/** Le bloc de synthèse que la colonne « Synthèse » des étapes rend sans enveloppe propre. */
function summaryBlock(context: CanvasContext): string | undefined {
  const page = shownPage(context);
  const blocks = page === undefined ? blocksOf(context.template) : blocksOn(context.template, page);
  return blocks.find((block) => blockComponent(block).name === 'FormSummary')?.block_id;
}

/** Page désignée par un onglet, une étape ou un titre de page du rendu. */
function pageOfNavigation(context: CanvasContext, element: Element): string | undefined {
  const tab = element.closest('.ay-tabs__tab');
  if (tab !== null && context.host.contains(tab)) return tab.id.startsWith('tab-') ? tab.id.slice(4) : undefined;
  const step = element.closest('.ay-staged__step');
  if (step !== null && context.host.contains(step)) {
    const index = [...context.host.querySelectorAll('.ay-staged__step')].indexOf(step);
    return context.pages[index]?.page_id;
  }
  const title = element.closest('h2.ay-layout__page-title');
  if (title !== null && context.host.contains(title)) return context.pages.find((page) => fr(page.label, page.page_id) === textOf(title))?.page_id;
  return undefined;
}

/** Champ d'une série sous un clic : le pavé ou le libellé cliqué, par le libellé du champ. */
function seriesFieldAt(template: Template, sectionId: string, element: Element | null): string | null {
  const s = template.sections.findIndex((section) => section.section_id === sectionId);
  const section = template.sections[s];
  const label = element?.closest('label');
  if (section === undefined || !grouped(section) || label === null || label === undefined) return null;
  const shown = textOf(label).toLocaleLowerCase('fr');
  // Le libellé le plus long l'emporte (« Poids brut » avant « Poids »).
  let f = -1;
  let best = 0;
  section.fields.forEach((entry, index) => {
    const wanted = text(entry.field.label, 'fr', '').trim().toLocaleLowerCase('fr');
    if (wanted !== '' && wanted.length > best && shown.startsWith(wanted)) {
      f = index;
      best = wanted.length;
    }
  });
  return f < 0 ? null : `field:/sections/${s}/fields/${f}`;
}

/**
 * Élément du gabarit sous un nœud du rendu : bloc, champ, partie, page (onglet, étape, fond de page),
 * sinon la page montrée (pages déclarées) ou le formulaire.
 */
export function selectionAt(context: CanvasContext, element: Element | null): string {
  const { template, host } = context;
  const blockIds = new Set(blocksOf(template).map((block) => block.block_id));
  const pages = declared(template);
  const page = (pageId: string | undefined): string => (pages && pageId !== undefined ? `page:${pageId}` : 'form');
  // Une cellule de bande ou de détail désigne son champ, quel que soit le contrôle cliqué (bouton, compteur).
  const cell = element === null ? null : fieldOfCell(context, element);
  if (cell !== null) return `field:${cell}`;
  for (let node: Element | null = element; node !== null && host.contains(node); node = node.parentElement) {
    const testId = node.getAttribute('data-testid');
    if (testId !== null && testId.startsWith('block-') && blockIds.has(testId.slice(6))) return `block:${testId.slice(6)}`;
    if (node.matches('aside.ay-summary')) {
      const summary = summaryBlock(context);
      if (summary !== undefined) return `block:${summary}`;
    }
    if (node.classList.contains('ay-form__entry')) {
      const pointer = fieldOfEntry(template, node);
      if (pointer !== null) return `field:${pointer}`;
    }
    const sectionId = sectionOfNode(template, node);
    if (sectionId !== null) return seriesFieldAt(template, sectionId, element) ?? `section:${sectionId}`;
    const container = sectionOfContainer(template, node);
    if (container !== null) return seriesFieldAt(template, container, element) ?? `section:${container}`;
    const navigated = pageOfNavigation(context, node);
    if (navigated !== undefined) return page(navigated);
    // Éditeur v2 : le bandeau du haut, la barre du bas et la barre des pages (onglets, étapes) se choisissent.
    if (node.matches('[data-testid="form-banner"]')) return 'header';
    if (node.matches('.ay-form-actions')) return 'footer';
    if (pages && node.matches('.ay-staged__steps, .ay-tabs__list')) return 'pages';
  }
  return page(shownPage(context));
}

const pageContainer = (host: HTMLElement): Element | null => host.querySelector('.ay-tabs__panel') ?? host.querySelector('.ay-staged__content') ?? host.querySelector('.ay-wizard') ?? host.querySelector('form.ay-form');

/**
 * Case d'un champ d'une série (saisie ligne par ligne) : son pavé de saisie ou son libellé, par le libellé
 * du champ ; sinon `null` (le contour reste sur la série).
 */
function cellOfSeriesField(owner: Element, label: string): Element | null {
  const wanted = label.trim().toLocaleLowerCase('fr');
  if (wanted === '') return null;
  const starts = (element: Element): boolean => textOf(element).toLocaleLowerCase('fr').startsWith(wanted);
  return [...owner.querySelectorAll('label')].find(starts) ?? [...owner.querySelectorAll('.ay-field, th, [role="columnheader"]')].find(starts) ?? null;
}

/** Nœud du rendu qui montre un élément (pour son contour), ou `null` s'il n'est pas à l'écran. */
export function elementFor(context: CanvasContext, id: string): Element | null {
  const { template, host } = context;
  if (id === 'header') return host.querySelector('[data-testid="form-banner"]');
  if (id === 'footer') return host.querySelector('.ay-form-actions');
  if (id === 'pages') return host.querySelector('.ay-staged__steps, .ay-tabs__list');
  if (id.startsWith('block:')) {
    const blockId = id.slice(6);
    const found = host.querySelector(`[data-testid="block-${escapeId(blockId)}"]`);
    if (found !== null) return found;
    return summaryBlock(context) === blockId ? host.querySelector('aside.ay-summary') : null;
  }
  if (id.startsWith('section:')) {
    const sectionId = id.slice(8);
    const placed = sectionNodes(host, sectionId)[0];
    if (placed !== undefined) return placed;
    const section = template.sections.find((candidate) => candidate.section_id === sectionId);
    if (section === undefined) return null;
    if (!grouped(section)) {
      for (const entry of section.fields) {
        const fieldset = entryOfField(host, entry.field.field_id)?.closest('fieldset.ay-form__section');
        if (fieldset !== null && fieldset !== undefined) return fieldset;
      }
      return null;
    }
    const label = text(section.label, 'fr', section.section_id);
    return [...host.querySelectorAll('h3.ay-form__repeat-title')].find((title) => textOf(title) === label)?.parentElement ?? null;
  }
  if (id.startsWith('field:')) {
    const pointer = id.slice(6);
    const segments = pointer.split('/');
    const section = template.sections[Number(segments[2])];
    if (section === undefined) return null;
    // Cellule marquée (bande de champs, détail d'une série) : le champ lui-même, avant tout repli.
    const marked = segments[3] === 'fields' ? section.fields[Number(segments[4])] : undefined;
    if (marked !== undefined) {
      const wanted = `[data-field="${escapeId(marked.field.field_id)}"]`;
      for (const owner of sectionNodes(host, section.section_id)) {
        const cell = owner.matches(wanted) ? owner : owner.querySelector(wanted);
        if (cell !== null) return cell;
      }
    }
    if (grouped(section) || segments[3] !== 'fields') {
      const owner = elementFor(context, `section:${section.section_id}`);
      const entry = segments[3] === 'fields' ? section.fields[Number(segments[4])] : undefined;
      return (entry === undefined || owner === null ? null : cellOfSeriesField(owner, text(entry.field.label, 'fr', ''))) ?? owner;
    }
    const entry = section.fields[Number(segments[4])];
    if (entry === undefined) return null;
    return entryOfField(host, entry.field.field_id);
  }
  if (id.startsWith('page:')) {
    const pageId = id.slice(5);
    const tab = host.querySelector(`#${escapeId(`tab-${pageId}`)}`);
    if (tab !== null) return tab;
    const index = context.pages.findIndex((page) => page.page_id === pageId);
    const step = index < 0 ? undefined : host.querySelectorAll('.ay-staged__step')[index];
    if (step !== undefined) return step;
    const label = fr(context.pages[index]?.label, '');
    const title = [...host.querySelectorAll('h2.ay-layout__page-title')].find((candidate) => textOf(candidate) === label);
    if (title !== undefined) return title;
    return pageId === shownPage(context) ? pageContainer(host) : null;
  }
  if (id === 'form') return host.querySelector('form.ay-form');
  return null;
}

/**
 * Page qu'un clic montre dans l'aperçu : un onglet, une étape, « Suivant » ou « Précédent ». Navigation
 * d'aperçu seulement : aucune saisie, aucun contrôle des manques (on parcourt un brouillon).
 */
export function navigationAt(context: CanvasContext, element: Element | null): string | null {
  if (element === null) return null;
  const pageId = (() => {
    const tabOrStep = element.closest('.ay-tabs__tab, .ay-staged__step');
    if (tabOrStep !== null) return pageOfNavigation(context, tabOrStep);
    const index = currentIndex(context);
    const next = element.closest('button.ay-form-actions__primary');
    if (next !== null && context.host.contains(next) && next.getAttribute('type') === 'button') return context.pages[index + 1]?.page_id;
    const previous = element.closest('.ay-form-actions__start button');
    if (previous !== null && context.host.contains(previous)) return context.pages[index - 1]?.page_id;
    return undefined;
  })();
  return pageId ?? null;
}

/** Libellé modifiable sous un double-clic : le nœud de texte et l'élément du gabarit qu'il nomme. */
export function editableAt(context: CanvasContext, element: Element | null): { id: string; part: LabelPart; element: Element } | null {
  if (element === null || !context.host.contains(element)) return null;
  const navigation = element.closest('.ay-tabs__tab, .ay-staged__step, h2.ay-layout__page-title');
  if (navigation !== null && declared(context.template)) {
    const pageId = pageOfNavigation(context, navigation);
    if (pageId === undefined) return null;
    const label = navigation.matches('.ay-staged__step') ? (navigation.querySelector('span:not(.ay-staged__step-num)') ?? navigation) : navigation.matches('.ay-tabs__tab') ? (navigation.querySelector('span') ?? navigation) : navigation;
    return { id: `page:${pageId}`, part: 'label', element: label };
  }
  const heading = element.closest('summary.ay-placed__toggle, legend, h3.ay-form__repeat-title');
  if (heading !== null && context.host.contains(heading)) {
    const owner = selectionAt(context, heading.parentElement);
    return owner.startsWith('section:') ? { id: owner, part: 'label', element: heading } : null;
  }
  const eyebrow = element.closest('.ay-banner-object__eyebrow, .ay-banner-object__label');
  if (eyebrow !== null) {
    const owner = selectionAt(context, eyebrow);
    if (owner.startsWith('block:')) return { id: owner, part: eyebrow.matches('.ay-banner-object__eyebrow') ? 'eyebrow' : 'label', element: eyebrow };
  }
  const label = element.closest('.ay-field__label, label, .ay-fv2-strip__label, .ay-fv2-detail__label');
  if (label !== null && label.closest('.ay-form__entry, [data-field]') !== null) {
    const owner = selectionAt(context, label);
    if (owner.startsWith('field:')) return { id: owner, part: 'label', element: label };
  }
  return null;
}

// ---- Dépôt.

export interface DropPoint {
  target: string;
  line: Box;
}

interface RegionHit {
  region: string;
  element: Element;
}

/** Région du gabarit sous un nœud : `data-region`, le bandeau (région d'en-tête), la colonne de synthèse. */
function regionAt(context: CanvasContext, element: Element): RegionHit | null {
  const body = currentLayout(context.registry, context.template)?.body;
  if (body === undefined) return null;
  const region = element.closest('[data-region]');
  if (region !== null && context.host.contains(region)) return { region: region.getAttribute('data-region')!, element: region };
  const banner = element.closest('.ay-form-banner');
  const header = body.regions.find((candidate) => candidate.role === 'header');
  if (banner !== null && header !== undefined && context.host.contains(banner)) return { region: header.region_id, element: banner.querySelector('.ay-form-banner__blocks') ?? banner };
  const aside = element.closest('aside.ay-summary');
  const summary = body.regions.find((candidate) => candidate.role === 'summary');
  if (aside !== null && summary !== undefined && context.host.contains(aside)) return { region: summary.region_id, element: aside };
  return null;
}

const regionTargetOf = (context: CanvasContext, region: string): string => {
  const page = shownPage(context);
  return `region:${region}${declared(context.template) && page !== undefined ? `@${page}` : ''}`;
};

/** Sections et blocs du rendu, par élément du gabarit. */
function closestNode(context: CanvasContext, element: Element, accept: (id: string) => boolean): { id: string; element: Element } | null {
  for (let node: Element | null = element; node !== null && context.host.contains(node); node = node.parentElement) {
    const id = selectionAt(context, node);
    if (accept(id) && elementFor(context, id) === node) return { id, element: node };
  }
  return null;
}

/** Point de dépôt sous le pointeur, dans l'aperçu, pour une sorte d'élément glissé. */
export function dropAt(context: CanvasContext, kind: DragKind, hit: Element | null, x: number, y: number): DropPoint | null {
  if (hit === null || !context.host.contains(hit)) return null;
  const { template, registry } = context;
  if (kind === 'page') {
    const pageId = pageOfNavigation(context, hit);
    const element = pageId === undefined ? null : elementFor(context, `page:${pageId}`);
    if (pageId === undefined || element === null) return null;
    const box = boxOf(element);
    const axis = axisOf(element);
    const side = sideOf(box, x, y, axis);
    const pages = declaredPages(template);
    const next = pages[pages.findIndex((page) => page.page_id === pageId) + 1];
    return { target: side === 'before' ? `before:page:${pageId}` : next === undefined ? 'end:pages' : `before:page:${next.page_id}`, line: lineAt(box, side, axis) };
  }
  if (kind === 'field') {
    const field = closestNode(context, hit, (id) => id.startsWith('field:') && /\/fields\/\d+$/.test(id) && !id.includes('/repeat/'));
    if (field !== null) {
      const box = boxOf(field.element);
      const axis = axisOf(field.element);
      const side = sideOf(box, x, y, axis);
      return { target: `${side}:field:${field.id.slice(6)}`, line: lineAt(box, side, axis) };
    }
    const section = closestNode(context, hit, (id) => id.startsWith('section:'));
    if (section === null) return null;
    const owner = template.sections.find((candidate) => `section:${candidate.section_id}` === section.id);
    if (owner === undefined || grouped(owner)) return null;
    return { target: section.id, line: lineInside(boxOf(section.element)) };
  }
  if (kind === 'section') {
    const section = closestNode(context, hit, (id) => id.startsWith('section:'));
    if (section !== null) {
      const box = boxOf(section.element);
      const side = sideOf(box, x, y, 'y');
      return { target: `${side}:section:${section.id.slice(8)}`, line: lineAt(box, side, 'y') };
    }
    const region = regionAt(context, hit);
    if (region !== null) return { target: regionTargetOf(context, region.region), line: lineInside(boxOf(region.element)) };
    const form = hit.closest('form.ay-form');
    if (form !== null && currentLayout(registry, template) === undefined) {
      const last = template.sections[template.sections.length - 1];
      const element = last === undefined ? null : elementFor(context, `section:${last.section_id}`);
      return { target: 'end:sections', line: element === null ? lineInside(boxOf(form)) : lineAt(boxOf(element), 'after', 'y') };
    }
    return null;
  }
  // Bloc : à côté d'un bloc, sinon à la fin des blocs de la région (avant ses parties).
  const block = closestNode(context, hit, (id) => id.startsWith('block:'));
  if (block !== null) {
    const box = boxOf(block.element);
    const axis = axisOf(block.element);
    const side = sideOf(box, x, y, axis);
    return { target: `${side}:block:${block.id.slice(6)}`, line: lineAt(box, side, axis) };
  }
  const region = regionAt(context, hit);
  if (region === null) return null;
  const body = currentLayout(registry, template)?.body;
  const page = shownPage(context);
  const inRegion = (page === undefined ? blocksOf(template) : blocksOn(template, page)).filter((candidate) => blockRegion(body, candidate.region) === region.region);
  const lastShown = inRegion.map((candidate) => elementFor(context, `block:${candidate.block_id}`)).filter((element): element is Element => element !== null && region.element.contains(element)).pop();
  const line = lastShown !== undefined ? lineAt(boxOf(lastShown), 'after', axisOf(lastShown)) : lineAt(boxOf(region.element), 'before', 'y');
  return { target: regionTargetOf(context, region.region), line };
}

/** Zones de dépôt visibles pendant un glisser : les régions du rendu qui acceptent la sorte glissée. */
export function visibleZones(context: CanvasContext, category: string): Array<{ region: string; box: Box; label: string }> {
  const body = currentLayout(context.registry, context.template)?.body;
  if (body === undefined) return [];
  const zones: Array<{ region: string; box: Box; label: string }> = [];
  const seen = new Set<string>();
  const candidates = [...context.host.querySelectorAll('[data-region], .ay-form-banner, aside.ay-summary')];
  for (const element of candidates) {
    const hit = regionAt(context, element);
    if (hit === null || seen.has(hit.region)) continue;
    const region = body.regions.find((candidate) => candidate.region_id === hit.region);
    if (region === undefined || region.role === 'footer' || !region.accepts.includes(category as never)) continue;
    seen.add(hit.region);
    zones.push({ region: hit.region, box: boxOf(hit.element), label: fr(region.label, 'Zone') });
  }
  return zones;
}

/**
 * Cibles sans place visible : régions vides ou absentes de la page montrée, autres pages. Elles vont au
 * dock du glisser (« Déposer dans : … »), pour qu'aucune place admise ne soit inatteignable.
 */
export function hiddenTargets(context: CanvasContext, kind: DragKind, category: string, visible: ReadonlySet<string>): Array<{ target: string; label: string }> {
  const { template, registry } = context;
  const body = currentLayout(registry, template)?.body;
  if (body === undefined || kind === 'field' || kind === 'page') return [];
  const targets: Array<{ target: string; label: string }> = [];
  for (const region of body.regions) {
    if (region.role === 'footer' || visible.has(region.region_id) || !region.accepts.includes(category as never)) continue;
    targets.push({ target: regionTargetOf(context, region.region_id), label: fr(region.label, 'Zone') });
  }
  if (declared(template)) {
    const here = shownPage(context);
    const main = kind === 'section' ? sectionRegion(body, undefined) : body.regions.find((candidate) => candidate.accepts.includes(category as never) && candidate.role !== 'footer')?.region_id;
    if (main !== undefined) {
      for (const page of declaredPages(template)) {
        if (page.page_id === here) continue;
        targets.push({ target: `region:${main}@${page.page_id}`, label: `Page « ${fr(page.label, 'Page')} »` });
      }
    }
  }
  return targets;
}

/** Point de dépôt sur une rangée des calques (avant ou après elle, ou dans une zone). */
export function layersDropAt(template: Template, kind: DragKind, hit: Element | null, x: number, y: number): DropPoint | null {
  const row = hit?.closest('.ay-studio-layers [data-node]');
  if (row === null || row === undefined) return null;
  const id = row.getAttribute('data-node') ?? '';
  const box = boxOf(row.querySelector('.ay-studio-layer__row') ?? row);
  const side = sideOf(box, x, y, 'y');
  const line = lineAt(box, side, 'y');
  const inside = lineInside(box);
  const prefix = id.split(':')[0];
  if (kind === 'block' && prefix === 'block') return { target: `${side}:block:${id.slice(6)}`, line };
  if (kind === 'block' && prefix === 'section') return { target: id, line: inside };
  if (kind === 'section' && prefix === 'section') return { target: `${side}:section:${id.slice(8)}`, line };
  if (kind === 'field' && prefix === 'field' && /\/fields\/\d+$/.test(id) && !id.includes('/repeat/')) return { target: `${side}:field:${id.slice(6)}`, line };
  if (kind === 'field' && prefix === 'section') {
    const section = template.sections.find((candidate) => `section:${candidate.section_id}` === id);
    return section === undefined || grouped(section) ? null : { target: id, line: inside };
  }
  if (kind === 'page' && prefix === 'page') {
    const pages = declaredPages(template);
    const next = pages[pages.findIndex((page) => `page:${page.page_id}` === id) + 1];
    return { target: side === 'before' ? `before:page:${id.slice(5)}` : next === undefined ? 'end:pages' : `before:page:${next.page_id}`, line };
  }
  if ((kind === 'block' || kind === 'section') && prefix === 'region') {
    // Rangée de zone : `region:<page>:<région>` ; « Sur toutes les pages » ne reçoit pas de dépôt.
    const [, pageId, regionId] = id.split(':');
    if (regionId === undefined) return null;
    return { target: `region:${regionId}${declared(template) && pageId !== undefined ? `@${pageId}` : ''}`, line: inside };
  }
  if (kind === 'section' && id === 'form') return { target: 'end:sections', line: inside };
  return null;
}

/** Nœud de texte du libellé d'un élément (pour « Renommer » sans double-clic). */
export function labelElementFor(context: CanvasContext, id: string): { element: Element; part: LabelPart } | null {
  const element = elementFor(context, id);
  if (element === null) return null;
  if (id.startsWith('page:')) {
    const label = element.matches('.ay-staged__step') ? element.querySelector('span:not(.ay-staged__step-num)') : element.matches('.ay-tabs__tab') ? element.querySelector('span') : element.matches('h2') ? element : null;
    return label === null ? null : { element: label, part: 'label' };
  }
  if (id.startsWith('section:')) {
    const heading = element.querySelector('summary.ay-placed__toggle, legend, h3.ay-form__repeat-title');
    return heading === null ? null : { element: heading, part: 'label' };
  }
  if (id.startsWith('field:')) {
    const label = element.querySelector('.ay-field__label, label, .ay-fv2-strip__label, .ay-fv2-detail__label');
    return label === null ? null : { element: label, part: 'label' };
  }
  if (id.startsWith('block:')) {
    const eyebrow = element.querySelector('.ay-banner-object__eyebrow');
    return eyebrow === null ? null : { element: eyebrow, part: 'eyebrow' };
  }
  return null;
}

/** Ligne d'insertion d'une cible (parcours au clavier), si sa place est à l'écran. */
export function lineForTarget(context: CanvasContext, target: string): Box | null {
  const relative = /^(before|after):(block|section|field|page):(.+)$/.exec(target);
  if (relative !== null) {
    const element = elementFor(context, `${relative[2]}:${relative[3]}`);
    if (element === null) return null;
    const axis = relative[2] === 'section' ? 'y' : axisOf(element);
    return lineAt(boxOf(element), relative[1] as 'before' | 'after', axis);
  }
  if (target.startsWith('region:')) {
    const [region] = target.slice(7).split('@');
    const element = context.host.querySelector(`[data-region="${escapeId(region ?? '')}"]`);
    return element === null ? null : lineInside(boxOf(element));
  }
  if (target.startsWith('section:')) {
    const element = elementFor(context, target);
    return element === null ? null : lineInside(boxOf(element));
  }
  if (target === 'end:pages') {
    const pages = declaredPages(context.template);
    const last = pages[pages.length - 1];
    const element = last === undefined ? null : elementFor(context, `page:${last.page_id}`);
    return element === null ? null : lineAt(boxOf(element), 'after', axisOf(element));
  }
  if (target === 'end:sections') {
    const last = context.template.sections[context.template.sections.length - 1];
    const element = last === undefined ? null : elementFor(context, `section:${last.section_id}`);
    return element === null ? null : lineAt(boxOf(element), 'after', 'y');
  }
  return null;
}
